import { Button, Loader, Select, Autocomplete, TextInput } from '@mantine/core';
import { ApiKey, insert, update } from '../api/apiKeys';
import { useAuth } from '../auth/AuthContext';
import { ChangeEvent, useState } from 'react';
import { toast } from 'react-toastify';
import { isLeft } from 'fp-ts/lib/Either';
import { useKnownApps } from '../hooks/useKnownApps';
import { useNavigate } from 'react-router-dom';
import { useAPIKeyOrgs } from '../hooks/useAPIKeyOrgs';

type FormData = {
  scope: string | null;
  organisation: string;
  contact: string;
};
export const ApiKeyForm = ({ apiKey }: { apiKey: ApiKey | null }) => {
  const [formData, setFormData] = useState<FormData>({
    scope: apiKey?.scope || '',
    organisation: apiKey?.organisation || '',
    contact: apiKey?.contact || '',
  });

  const navigate = useNavigate();
  const auth = useAuth();
  const { knownApps } = useKnownApps();
  const { organisations } = useAPIKeyOrgs();

  const inputChanged = (key: string) => (e: ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [key]: e.target.value });
  };

  const orgChanged = (organisation: string) => {
    setFormData({ ...formData, organisation });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!auth?.token) {
      toast.error('Missing required user details. Please try again later');
      return;
    }

    if (!formData.scope) {
      toast.error('Scope is required');
      return;
    }
    // Faffy types. Doesn't love the null check above.
    const record = { ...formData, scope: formData.scope as string };

    const result = apiKey
      ? await update(auth.token, apiKey._id, record)
      : await insert(auth.token, record);

    if (isLeft(result)) {
      toast.error(result.left.message);
    } else if (apiKey) {
      toast.success('API key updated');
    } else {
      toast.success('API key created');
      navigate(`/api-keys/${result.right._id}`);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Select
        name="scope"
        label="Scope"
        required
        rightSection={knownApps === null ? <Loader size={12} /> : null}
        data={knownApps ?? []}
        value={formData.scope}
        onChange={(value) => setFormData({ ...formData, scope: value })}
      />
      <Autocomplete
        name="organisation"
        label="Organisation"
        value={formData.organisation}
        onChange={orgChanged}
        data={organisations ?? []}
        required
      />
      <TextInput
        name="contact"
        label="Contact"
        value={formData.contact}
        onChange={inputChanged('contact')}
        required
      />
      <Button mt="md" type="submit">
        {apiKey ? 'Save API key' : 'Add API key'}
      </Button>
    </form>
  );
};
